import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api, formatCOP, imageUrl } from '../services/api';
import usePickList, { itemPickKey } from '../hooks/usePickList';
import { isNetworkError } from '../utils/offlineBodegaQueue';
import { entregaParts, pedidoDatosFicha } from '../utils/entregaAddress';
import ImageZoomLightbox from '../components/ImageZoomLightbox';

const FACT_KEY = 'oh_factura_quien';

const ESTADO_FACTURA = {
  pendiente: 'Por facturar',
  en_proceso: 'Facturando',
  facturado: 'Facturado',
};

function fmtFecha(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function loadSaved(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function facturaEstadoOf(pedido) {
  const st = String(pedido?.facturacion_estado || '').trim().toLowerCase();
  if (st === 'en_proceso' || st === 'facturado') return st;
  return 'pendiente';
}

function esFacturaIva(pedido) {
  return String(pedido?.tipo_documento || '').trim().toLowerCase() === 'iva';
}

function labelDocumento(pedido) {
  return esFacturaIva(pedido) ? 'Número de factura' : 'Código de remisión';
}

function parseKeySet(raw) {
  if (Array.isArray(raw)) return new Set(raw.map(String));
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch {
      return new Set();
    }
  }
  return new Set();
}

export default function FacturacionReview({ id, pin, onUpdated, onBack }) {
  const saved = loadSaved(FACT_KEY);
  const [pedido, setPedido] = useState(null);
  const [zoomPhoto, setZoomPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [equipo, setEquipo] = useState([]);
  const [facturadorId, setFacturadorId] = useState(() => saved?.id || '');
  const [facturadorNombre, setFacturadorNombre] = useState(() => saved?.nombre || '');
  const [docNumero, setDocNumero] = useState('');
  const pickingRef = useRef(false);
  const docDirty = useRef(false);
  const docTimer = useRef(null);

  const items = pedido?.items || [];
  const factEstado = facturaEstadoOf(pedido);
  const yoFacturando = factEstado === 'en_proceso';
  pickingRef.current = yoFacturando;
  const canEdit = yoFacturando;
  const canastaWho = pedido?.facturado_por_nombre || facturadorNombre || null;

  const {
    picked,
    missing,
    toggle,
    markMissing,
    done,
    donePicked,
    doneMissing,
    total,
    allDone,
    progress,
    saveError,
    savedOffline,
  } = usePickList(id, items, pin, canEdit ? canastaWho : null, {
    readOnly: !canEdit,
  });

  const reload = useCallback(async () => {
    const res = await api.getPedido(id);
    setPedido(res.data);
    if (!docDirty.current) {
      setDocNumero(String(res.data?.documento_numero || ''));
    }
    return res.data;
  }, [id, pin]);

  useEffect(() => {
    docDirty.current = false;
    setDocNumero('');
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getSeparadores();
        if (!cancelled) setEquipo(res.data || []);
      } catch {
        if (!cancelled) setEquipo([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        await reload();
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, pin, reload]);

  useEffect(() => {
    if (!id || !pin) return undefined;
    const t = setInterval(() => {
      if (pickingRef.current) return;
      reload().catch(() => {});
    }, 45000);
    const onVis = () => {
      if (document.visibilityState === 'visible' && !pickingRef.current) reload().catch(() => {});
    };
    const onInv = (ev) => {
      const domains = ev.detail?.domains || [];
      if (!domains.includes('order')) return;
      if (pickingRef.current) return;
      reload().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('oh:invalidate', onInv);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('oh:invalidate', onInv);
    };
  }, [id, pin, reload]);

  function onPickFacturador(e) {
    const val = e.target.value;
    const found = equipo.find((s) => String(s.id) === String(val));
    if (found) {
      setFacturadorId(found.id);
      setFacturadorNombre(found.nombre);
      sessionStorage.setItem(FACT_KEY, JSON.stringify({ id: found.id, nombre: found.nombre }));
    } else {
      setFacturadorId('');
      setFacturadorNombre('');
    }
  }

  function onDocNumeroChange(e) {
    docDirty.current = true;
    setDocNumero(e.target.value);
  }

  useEffect(() => {
    if (!id || !pin || !pedido || facturaEstadoOf(pedido) === 'facturado' || !docDirty.current) {
      return undefined;
    }
    clearTimeout(docTimer.current);
    docTimer.current = setTimeout(async () => {
      try {
        await api.saveDocumentoNumero(id, String(docNumero || '').trim());
        docDirty.current = false;
      } catch {
        /* se reintenta al terminar */
      }
    }, 450);
    return () => clearTimeout(docTimer.current);
  }, [docNumero, id, pin, pedido]);

  async function empezar() {
    if (!facturadorNombre) {
      setError('Elige quién factura el pedido');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await api.iniciarFacturacion(id, { facturadorId, facturadorNombre });
      setPedido(res.data);
      onUpdated?.();
    } catch (err) {
      setError(err.message || 'No se pudo iniciar la facturación');
      if (err.status === 409 || err.status === 400) {
        try {
          await reload();
        } catch {
          /* ignore */
        }
      }
    } finally {
      setSaving(false);
    }
  }

  async function terminar() {
    if (!facturadorNombre && !pedido?.facturado_por_nombre) {
      setError('Elige quién factura el pedido');
      return;
    }
    if (!allDone && total > 0) {
      setError(`Faltan ${total - done} producto(s) por marcar`);
      return;
    }
    const doc = String(docNumero || '').trim();
    if (!doc) {
      setError(`Escribe el ${labelDocumento(pedido).toLowerCase()}`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.saveCanasta(id, [...picked], canastaWho, undefined, [...missing]);
      const res = await api.completarFacturacion(id, {
        facturadorId: facturadorId || pedido?.facturado_por_id,
        facturadorNombre: facturadorNombre || pedido?.facturado_por_nombre,
        documentoNumero: doc,
      });
      setPedido(res.data);
      onUpdated?.();
    } catch (err) {
      const detail =
        err?.code === 'FACTURA_INCOMPLETA' ? err.message : err.message || 'No se pudo terminar';
      setError(detail);
      if (isNetworkError(err) || err.status === 409 || err.status === 400) {
        try {
          await reload();
        } catch {
          /* ignore */
        }
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="review">
        <p className="muted center">Cargando pedido...</p>
      </div>
    );
  }
  if (error && !pedido) {
    return (
      <div className="review">
        <p className="error center">{error}</p>
        <button type="button" className="btn-back" onClick={onBack}>
          ← Volver
        </button>
      </div>
    );
  }
  if (!pedido) {
    return (
      <div className="review">
        <p className="empty center">Pedido no encontrado</p>
      </div>
    );
  }

  const totalUnidades = items.reduce((s, it) => s + (Number(it.cantidad) || 0), 0);
  const ficha = pedidoDatosFicha(pedido);
  const entrega = entregaParts(pedido);
  const bodegaMissing = parseKeySet(pedido.missing_keys);
  const bodegaPicked = parseKeySet(pedido.pick_keys);
  const quien = pedido.facturado_por_nombre || facturadorNombre;
  const startBlocked = factEstado === 'pendiente' && !facturadorNombre;

  return (
    <div className="review">
      <header className="review-head">
        <button type="button" className="btn-back" onClick={onBack}>
          ← Lista
        </button>
        <div className="review-head-text">
          <h2>{pedido.numero}</h2>
          <p className="muted">
            {ESTADO_FACTURA[factEstado] || factEstado}
            {esFacturaIva(pedido) ? ' · Factura' : ' · Remisión'}
            {String(docNumero || pedido.documento_numero || '').trim()
              ? ` · ${String(docNumero || pedido.documento_numero).trim()}`
              : ''}
          </p>
        </div>
      </header>

      <div className="review-ficha">
        <section>
          <h3>Cliente / sucursal</h3>
          <p>
            <span>Nombre</span>
            <strong>
              {ficha.nombre}
              {ficha.esSede ? <em className="sede-pill">Sucursal</em> : null}
            </strong>
          </p>
          <p>
            <span>Cód.</span>
            <strong>{ficha.codigo}</strong>
          </p>
          <p>
            <span>Tel.</span>
            <strong>{ficha.telefono}</strong>
          </p>
          <p>
            <span>Dirección</span>
            <strong>{ficha.direccion}</strong>
          </p>
          {ficha.ciudadZona ? (
            <p>
              <span>Ciudad</span>
              <strong>{ficha.ciudadZona}</strong>
            </p>
          ) : null}
          {ficha.sedeEtiqueta ? (
            <p>
              <span>Sede</span>
              <strong>{ficha.sedeEtiqueta}</strong>
            </p>
          ) : null}
        </section>
        <section>
          <h3>Condiciones</h3>
          <p>
            <span>Vendedor</span>
            <strong>{ficha.vendedor}</strong>
          </p>
          <p>
            <span>Pago</span>
            <strong>{ficha.pago}</strong>
          </p>
          <p>
            <span>Horario recibo</span>
            <strong>{ficha.horario}</strong>
          </p>
          {ficha.transportadora ? (
            <p>
              <span>Transportadora</span>
              <strong>{ficha.transportadora}</strong>
            </p>
          ) : null}
        </section>
        <section className="review-ficha-obs">
          <h3>Observaciones</h3>
          <p>{ficha.observaciones || 'Sin observaciones'}</p>
        </section>
      </div>

      <div className="doc-numero-card">
        <label className="separador-pick">
          <span>{labelDocumento(pedido)}</span>
          {factEstado === 'facturado' ? (
            <strong className="doc-numero-value">
              {String(pedido.documento_numero || docNumero || '').trim() || '—'}
            </strong>
          ) : (
            <input
              className="doc-numero-input"
              type="text"
              inputMode="text"
              autoComplete="off"
              maxLength={60}
              value={docNumero}
              onChange={onDocNumeroChange}
              placeholder={
                esFacturaIva(pedido)
                  ? 'Escribe el número de la factura'
                  : 'Escribe el código de la remisión'
              }
            />
          )}
        </label>
      </div>

      {error ? <p className="error banner">{error}</p> : null}

      {yoFacturando ? (
        <div className="claim-banner claim-mio">
          <strong>Estás facturando este pedido ({quien})</strong>
        </div>
      ) : null}

      {factEstado === 'facturado' ? (
        <div className="completado-banner">
          <span className="completado-icon">✓</span>
          <div>
            <strong>Pedido FACTURADO</strong>
            <p>
              {pedido.facturado_por_nombre ? `Facturado por ${pedido.facturado_por_nombre}` : ''}
              {String(pedido.documento_numero || docNumero || '').trim()
                ? ` · ${labelDocumento(pedido)} ${String(pedido.documento_numero || docNumero).trim()}`
                : ''}
              {pedido.facturado_inicio_at ? ` · Inicio ${fmtFecha(pedido.facturado_inicio_at)}` : ''}
              {pedido.facturado_fin_at ? ` · Fin ${fmtFecha(pedido.facturado_fin_at)}` : ''}
            </p>
          </div>
        </div>
      ) : null}

      <div className="pick-summary">
        <span>
          <strong>{items.length}</strong> productos
        </span>
        <span>
          <strong>{totalUnidades}</strong> unidades
        </span>
        <span>{formatCOP(pedido.subtotal)}</span>
      </div>

      {factEstado !== 'facturado' && total > 0 ? (
        <div className="canasta-progress">
          <div className="canasta-progress-head">
            <strong>Revisión de facturación</strong>
            <span>
              {done} de {total} listos
              {doneMissing > 0 ? ` · ${doneMissing} sin producto` : ''}
            </span>
          </div>
          <div className="canasta-progress-bar">
            <div className="canasta-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          {allDone ? (
            <p className="canasta-done-msg">
              ✓ Todo marcado
              {doneMissing > 0 ? ` (${donePicked} ok · ${doneMissing} no hay)` : ''}
            </p>
          ) : (
            <p className="canasta-hint">
              Marca cada producto como en factura, o “No hay” si falta
            </p>
          )}
          {saveError ? <p className="error banner">{saveError}</p> : null}
          {savedOffline && !saveError ? (
            <p className="muted banner">Marcas en tablet — se sincronizan al volver internet.</p>
          ) : null}
        </div>
      ) : null}

      <div className="pick-list">
        <h3>Productos a facturar</h3>
        {!items.length ? (
          <p className="muted">Este pedido no tiene productos.</p>
        ) : (
          <ul>
            {items.map((it, idx) => {
              const key = itemPickKey(it, idx);
              const isMissing =
                factEstado === 'facturado' ? parseKeySet(pedido.factura_missing_keys).has(key) : missing.has(key);
              const inCanasta =
                !isMissing && (factEstado === 'facturado' || picked.has(key));
              const bodegaNoHay = bodegaMissing.has(key);
              const bodegaOk = !bodegaNoHay && bodegaPicked.has(key);
              return (
                <li
                  key={key}
                  className={`pick-item ${inCanasta ? 'pick-item-done' : ''} ${
                    isMissing ? 'pick-item-missing' : ''
                  }`}
                >
                  <div className="pick-actions">
                    {canEdit ? (
                      <>
                        <button
                          type="button"
                          className={`pick-check ${inCanasta ? 'checked' : ''}`}
                          onClick={() => toggle(key)}
                          aria-pressed={inCanasta}
                        >
                          <span className="pick-check-icon">{inCanasta ? '✓' : '○'}</span>
                          <span className="pick-check-label">
                            {inCanasta ? 'En factura' : 'Marcar'}
                          </span>
                        </button>
                        <button
                          type="button"
                          className={`pick-missing-btn ${isMissing ? 'active' : ''}`}
                          onClick={() => markMissing(key)}
                          aria-pressed={isMissing}
                        >
                          {isMissing ? 'Sin producto' : 'No hay'}
                        </button>
                      </>
                    ) : (
                      <span
                        className={`pick-check ${inCanasta || isMissing ? 'checked' : ''} ${
                          isMissing ? 'missing' : ''
                        } pick-check-readonly`}
                      >
                        <span className="pick-check-icon">
                          {isMissing ? '!' : inCanasta ? '✓' : '○'}
                        </span>
                        {isMissing ? (
                          <span className="pick-check-label">Sin producto</span>
                        ) : inCanasta ? (
                          <span className="pick-check-label">En factura</span>
                        ) : null}
                      </span>
                    )}
                  </div>

                  <div className="pick-item-photo">
                    {it.imagen ? (
                      <button
                        type="button"
                        className="pick-item-photo-btn"
                        onPointerUp={(e) => {
                          if (e.button != null && e.button !== 0) return;
                          e.preventDefault();
                          e.stopPropagation();
                          const src = imageUrl(it.imagen);
                          if (!src) return;
                          setZoomPhoto({
                            src,
                            alt: it.nombre || it.codigo || 'Producto',
                          });
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                        aria-label={`Abrir foto ${it.codigo || ''}`}
                        title="Toca para ver la foto grande"
                      >
                        <img src={imageUrl(it.imagen)} alt="" draggable={false} />
                        <span className="pick-photo-zoom-badge">🔍 Ver foto</span>
                      </button>
                    ) : (
                      <div className="pick-item-img-empty" aria-hidden="true" />
                    )}
                  </div>

                  <div className="pick-item-info">
                    <strong>{it.codigo}</strong>
                    <span>{it.nombre}</span>
                    {bodegaNoHay ? (
                      <em className="pick-missing-tag">Bodega: no hay</em>
                    ) : bodegaOk ? (
                      <em className="pick-bodega-ok">Bodega: en canasta</em>
                    ) : null}
                    {isMissing ? <em className="pick-missing-tag">Facturación: no hay</em> : null}
                    {it.observaciones ? (
                      <em className="pick-item-obs">Obs: {it.observaciones}</em>
                    ) : null}
                  </div>

                  <div className="pick-qty">× {it.cantidad}</div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {pedido.notas?.trim() ? (
        <div className="bodega-notas-card">
          <h4>Observaciones del vendedor</h4>
          <p style={{ whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>{pedido.notas}</p>
        </div>
      ) : null}

      {pedido.notas_bodega?.trim() ? (
        <div className="bodega-notas-card">
          <h4>Notas de bodega</h4>
          <p style={{ whiteSpace: 'pre-wrap', margin: '6px 0 0' }}>{pedido.notas_bodega}</p>
        </div>
      ) : null}

      <div className="sep-card sep-card-inline">
        <h4>Registro</h4>
        {entrega.direccion || entrega.ciudadZona ? (
          <div className="sep-entrega">
            <span>Dirección de entrega</span>
            {entrega.direccion ? <strong>{entrega.direccion}</strong> : null}
            {entrega.ciudadZona ? <em>{entrega.ciudadZona}</em> : null}
          </div>
        ) : null}
        {pedido.preparado_por_nombre ? (
          <p className="sep-row">
            <span>Separado por</span>
            <strong>{pedido.preparado_por_nombre}</strong>
          </p>
        ) : null}
        {factEstado === 'pendiente' ? (
          <label className="separador-pick">
            <span>Quién factura</span>
            <select value={facturadorId || ''} onChange={onPickFacturador}>
              <option value="">— Elige del equipo —</option>
              {equipo.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nombre}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="sep-row">
            <span>Quién factura</span>
            <strong>{pedido.facturado_por_nombre || facturadorNombre || '—'}</strong>
          </p>
        )}
        <p className="sep-row">
          <span>{labelDocumento(pedido)}</span>
          <strong>{String(docNumero || pedido.documento_numero || '').trim() || '—'}</strong>
        </p>
        <p className="sep-row">
          <span>Hora inicio</span>
          <strong>{fmtFecha(pedido.facturado_inicio_at)}</strong>
        </p>
        <p className="sep-row">
          <span>Hora fin</span>
          <strong>{fmtFecha(pedido.facturado_fin_at)}</strong>
        </p>
      </div>

      {factEstado !== 'facturado' ? (
        <div className="review-sticky-bar">
          {factEstado === 'pendiente' ? (
            <>
              <button
                type="button"
                className="btn-primary btn-block btn-action-main"
                disabled={saving || startBlocked}
                onClick={empezar}
              >
                {saving ? 'Guardando...' : '▶ Empezar a facturar'}
              </button>
              {startBlocked ? (
                <p className="sticky-hint">Elige quién factura antes de empezar</p>
              ) : null}
            </>
          ) : null}
          {yoFacturando ? (
            <>
              <p className="sticky-hint">Marca los productos y termina la facturación</p>
              <button
                type="button"
                className={`btn-primary btn-block btn-action-main btn-completado ${allDone ? 'pulse' : ''}`}
                disabled={saving || !allDone || !String(docNumero || '').trim()}
                onClick={terminar}
              >
                {saving ? 'Guardando...' : '✓ Terminar facturación'}
              </button>
              {!allDone ? (
                <p className="sticky-hint">Faltan productos por marcar</p>
              ) : !String(docNumero || '').trim() ? (
                <p className="sticky-hint">Escribe el {labelDocumento(pedido).toLowerCase()}</p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {zoomPhoto?.src ? (
        <ImageZoomLightbox
          src={zoomPhoto.src}
          alt={zoomPhoto.alt}
          onClose={() => setZoomPhoto(null)}
        />
      ) : null}
    </div>
  );
}
